import { BLOCK_CODE_LINE, BLOCK_PARAGRAPH, code, moveToNextLeafBlock } from '@gitbook/doc-core';
import {
    type Block,
    type Change,
    Document,
    isBlockRangeSelection,
    TextRange,
} from '@gitbook/slate';
import {
    findNode,
    getEventTransfer,
    type Plugin,
    preventBlockSelection,
} from '@gitbook/slate-react';

import {
    isBackspace,
    isEnter,
    isModA,
    isModEnter,
    isShiftTab,
    isTab,
    selectAllInContainerFirst,
} from '../common';

export const plugin: Plugin = {
    onKeyDown,
    onPaste,
    onMouseDown,
};

// By default, block selection treats each CodeBlockLine as a block,
// so using SHIFT + Click would select the entire code block.
// We don’t want that behavior inside code blocks. Instead, we want the
// normal text selection to work (so users can select part of the code).
// To achieve this, we intercept the event and prevent block selection.
function onMouseDown(event: MouseEvent, change: Change): boolean | void {
    const { value } = change;

    // When pressing shift
    if (!event.shiftKey) {
        return;
    }

    // If the target is an HTML element
    if (!(event.target instanceof HTMLElement)) {
        return;
    }

    // And there is a node corresponding to the element
    const node = findNode(event.target, value);
    if (!node) {
        return;
    }

    // And there is a parent block to the node
    const block = node.object === 'block' ? node : value.document.getClosestBlock(node.key);
    if (!block) {
        return;
    }

    // And that block is a code line
    if (block.type !== BLOCK_CODE_LINE) {
        return;
    }

    // And we are not in a block range
    if (isBlockRangeSelection(value.selection)) {
        return;
    }

    // And the current node is code
    const currentCode = code.getCurrentCode(value);
    if (!currentCode) {
        return;
    }

    // Prevent the block selection to happen
    preventBlockSelection(event);
}

function onKeyDown(event: KeyboardEvent, change: Change) {
    const { value } = change;
    if (isBlockRangeSelection(value.selection)) {
        return;
    }

    const currentCode = code.getCurrentCode(value);
    if (!currentCode) {
        return;
    }

    if (isModA(event)) {
        event.preventDefault();
        return selectAllInContainerFirst(change, () => {
            const firstText = currentCode.getFirstText();
            const lastText = currentCode.getLastText();
            if (!firstText || !lastText) {
                return null;
            }

            return TextRange.create().moveToRangeOf(firstText, lastText);
        });
    }
    if (isShiftTab(event)) {
        // User is pressing Shift+Tab
        return onShiftTab(currentCode, event, change);
    }
    if (isTab(event)) {
        // User is pressing Tab
        return onTab(currentCode, event, change);
    }
    if (isModEnter(event)) {
        // User is pressing Mod+Enter
        return onModEnter(currentCode, event, change);
    }
    if (isEnter(event)) {
        // User is pressing Enter
        return onEnter(currentCode, event, change);
    }
    if (isBackspace(event)) {
        // User is pressing Backspace
        return onBackspace(currentCode, event, change);
    }
}

function onPaste(event: ClipboardEvent, change: Change) {
    const { value } = change;
    if (isBlockRangeSelection(value.selection)) {
        return;
    }

    const currentCode = code.getCurrentCode(change.value);

    // Only handle paste when selection is completely a code block
    const { endBlock } = value;

    if (!currentCode || !endBlock || !currentCode.hasDescendant(endBlock.key)) {
        return undefined;
    }

    const data = getEventTransfer(event, { ignoreMetadata: true }); // we don't want to include syntax metadata as gets pasted into the code block

    // Convert to text if needed
    let text: string;

    if (data.type === 'fragment' && data.fragment) {
        text = data.fragment
            .getTexts()
            .map((t) => t.text)
            .join('\n');
    } else {
        text = data.text || '';
    }
    const linesOfCode = code.createCodeBlock(text).blockNodes;
    const fragment = Document.create({ nodes: linesOfCode });
    return change.insertDocument(fragment);
}

/**
 * Insert a tab after detecting it from code block content.
 */
function onTab(currentCode: Block, event: Event, change: Change): void | Change {
    const { value } = change;
    event.preventDefault();
    event.stopPropagation();

    const { isCollapsed } = value;
    const indent = code.getIndent(code.getCodeText(currentCode));

    // Selection is collapsed, we just insert an indent at cursor
    if (isCollapsed) {
        return change.insertText(indent).focus();
    }

    // We indent all selected lines
    return code.indentLines(change, indent);
}

/**
 * Reduce indentation in the selected lines.
 */
function onShiftTab(currentCode: Block, event: any, change: Change): void | Change {
    event.preventDefault();
    event.stopPropagation();

    const indent = code.getIndent(code.getCodeText(currentCode));

    // We dedent all selected lines
    return code.dedentLines(change, indent);
}

/**
 * Insert a new code line and start it with the indentation from previous line
 */
function onEnter(_currentCode: Block, event: Event, change: Change): void | Change {
    const result = code.splitCodeLine(change);
    if (result) {
        event.preventDefault();
        return result;
    }
}

/**
 * Exit the current code block
 */
function onModEnter(currentCode: Block, event: Event, change: Change): void | Change {
    const { value } = change;

    if (!value.isCollapsed) {
        return undefined;
    }

    event.preventDefault();

    moveToNextLeafBlock(change, currentCode);
    return change;
}

/**
 * Remove last indentation before cursor
 */
function onBackspace(currentCode: Block, event: Event, change: Change): void | Change {
    const { value } = change;

    if (value.isExpanded) {
        return undefined;
    }

    const { startOffset, startText } = value;

    const currentLine = value.startBlock;
    if (!currentLine) {
        return undefined;
    }

    // Detect and remove indentation at cursor
    const indent = code.getIndent(code.getCodeText(currentCode));
    const beforeSelection = currentLine.text.slice(0, startOffset);

    // If the line before selection ending with the indentation?
    if (beforeSelection.endsWith(indent)) {
        // Remove indent
        event.preventDefault();

        return change.deleteBackward(indent.length).focus();
    }

    // Otherwise check if we are in an empty code container...
    const isStartOfCode = startOffset === 0 && currentCode.getFirstText() === startText;
    // PERF: avoid checking for whole currentCode.text
    const isEmpty = currentCode.nodes.size === 1 && currentLine.text.length === 0;

    if (isStartOfCode && isEmpty) {
        event.preventDefault();
        // Convert it to default exit type
        return change
            .setBlocks(BLOCK_PARAGRAPH, { normalize: false })
            .unwrapNodeByKey(currentLine.key);
    }

    return undefined;
}
